import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Slider } from "../../components/ui/slider"
import { Button } from "../../components/ui/button"
import { ChevronRight, Wallet, PiggyBank, TrendingUp } from 'lucide-react';

const BudgetTraining = () => {
  // State for different training levels
  const [currentLevel, setCurrentLevel] = useState('beginner');
  const [beginnerStep, setBeginnerStep] = useState(0);
  
  // Personal budget state
  const [monthlyIncome, setMonthlyIncome] = useState(5000);
  const [essentialsPercentage, setEssentialsPercentage] = useState(50);
  const [savingsPercentage, setSavingsPercentage] = useState(20);
  const [discretionaryPercentage, setDiscretionaryPercentage] = useState(30);

  // Advanced budget state
  const [monthlyExpenses, setMonthlyExpenses] = useState({
    housing: 1500,
    utilities: 200,
    groceries: 400,
    transportation: 300,
    healthcare: 200,
    savings: 1000,
    entertainment: 300,
    dining: 200,
    shopping: 200,
    misc: 100
  });
  const [monthlyGoal, setMonthlyGoal] = useState(4000);
  const [emergencyFundGoal, setEmergencyFundGoal] = useState(15000);

  // Color constants
  const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  // Beginner level content
  const beginnerSteps = [
    {
      title: "What is Budgeting?",
      content: (
        <div className="space-y-4">
          <p>Budgeting is the process of creating a plan to spend your money wisely. It helps you:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Track income and expenses</li>
            <li>Make informed spending decisions</li>
            <li>Achieve financial goals</li>
            <li>Build long-term wealth</li>
          </ul>
          <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
            <Wallet className="h-8 w-8 text-blue-500" />
            <p className="font-medium">A budget is a roadmap for your financial journey</p>
          </div>
        </div>
      )
    },
    {
      title: "The 50/30/20 Rule",
      content: (
        <div className="space-y-4">
          <p>One popular budgeting framework is the 50/30/20 rule:</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4 bg-blue-50">
              <h3 className="font-bold mb-2">50% - Essentials</h3>
              <p className="text-sm">Housing, utilities, groceries, insurance</p>
            </Card>
            <Card className="p-4 bg-green-50">
              <h3 className="font-bold mb-2">30% - Discretionary</h3>
              <p className="text-sm">Entertainment, dining, shopping, hobbies</p>
            </Card>
            <Card className="p-4 bg-yellow-50">
              <h3 className="font-bold mb-2">20% - Savings</h3>
              <p className="text-sm">Emergency fund, retirement, investments</p>
            </Card>
          </div>
        </div>
      )
    },
    {
      title: "Benefits of Budgeting",
      content: (
        <div className="space-y-4">
          <p>Regular budgeting provides numerous benefits:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-bold mb-2">Financial Control</h4>
              <ul className="list-disc pl-4 space-y-1">
                <li>Clear spending awareness</li>
                <li>Reduced financial stress</li>
                <li>Better decision making</li>
              </ul>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-bold mb-2">Goal Achievement</h4>
              <ul className="list-disc pl-4 space-y-1">
                <li>Faster debt repayment</li>
                <li>Emergency preparedness</li>
                <li>Investment growth</li>
              </ul>
            </div>
          </div>
        </div>
      )
    }
  ];

  // Personal budget calculations
  const personalBudgetData = useMemo(() => {
    return [
      { name: 'Essentials', value: monthlyIncome * (essentialsPercentage / 100) },
      { name: 'Discretionary', value: monthlyIncome * (discretionaryPercentage / 100) },
      { name: 'Savings', value: monthlyIncome * (savingsPercentage / 100) }
    ];
  }, [monthlyIncome, essentialsPercentage, discretionaryPercentage, savingsPercentage]);

  // Advanced budget calculations
  const advancedBudgetData = useMemo(() => {
    const totalExpenses = Object.values(monthlyExpenses).reduce((sum, value) => sum + value, 0);
    const monthlyPerformance = monthlyGoal - totalExpenses;
    
    const expenseData = Object.entries(monthlyExpenses).map(([category, amount]) => ({
      category: category.charAt(0).toUpperCase() + category.slice(1),
      amount,
      percentage: (amount / totalExpenses * 100).toFixed(1)
    }));

    return {
      expenses: expenseData,
      total: totalExpenses,
      performance: monthlyPerformance,
      emergencyFundMonths: emergencyFundGoal / (totalExpenses - monthlyExpenses.savings)
    };
  }, [monthlyExpenses, monthlyGoal, emergencyFundGoal]);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PiggyBank className="h-6 w-6" />
            Interactive Budget Training
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={currentLevel} onValueChange={setCurrentLevel}>
            <TabsList className="w-full">
              <TabsTrigger value="beginner" className="flex-1">Beginner</TabsTrigger>
              <TabsTrigger value="personal" className="flex-1">Personal</TabsTrigger>
              <TabsTrigger value="advanced" className="flex-1">Advanced</TabsTrigger>
            </TabsList>

            {/* Beginner Level Content */}
            <TabsContent value="beginner">
              <Card>
                <CardHeader>
                  <CardTitle>{beginnerSteps[beginnerStep].title}</CardTitle>
                </CardHeader>
                <CardContent>
                  {beginnerSteps[beginnerStep].content}
                  <div className="flex justify-between mt-6">
                    <Button
                      variant="outline"
                      onClick={() => setBeginnerStep(Math.max(0, beginnerStep - 1))}
                      disabled={beginnerStep === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={() => {
                        if (beginnerStep === beginnerSteps.length - 1) {
                          setCurrentLevel('personal');
                        } else {
                          setBeginnerStep(beginnerStep + 1);
                        }
                      }}
                    >
                      {beginnerStep === beginnerSteps.length - 1 ? 'Next Level' : 'Next'}
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Personal Level Content */}
            <TabsContent value="personal">
              <Card>
                <CardHeader>
                  <CardTitle>Personal Budget Calculator</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Monthly Income: ${monthlyIncome}</label>
                      <Slider
                        value={[monthlyIncome]}
                        onValueChange={(values) => setMonthlyIncome(values[0])}
                        min={1000}
                        max={10000}
                        step={100}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Essentials: {essentialsPercentage}%</label>
                      <Slider
                        value={[essentialsPercentage]}
                        onValueChange={(values) => {
                          setEssentialsPercentage(values[0]);
                          const remaining = 100 - values[0];
                          setSavingsPercentage(Math.min(savingsPercentage, remaining));
                          setDiscretionaryPercentage(remaining - Math.min(savingsPercentage, remaining));
                        }}
                        min={0}
                        max={80}
                        step={1}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Savings: {savingsPercentage}%</label>
                      <Slider
                        value={[savingsPercentage]}
                        onValueChange={(values) => {
                          setSavingsPercentage(values[0]);
                          setDiscretionaryPercentage(100 - essentialsPercentage - values[0]);
                        }}
                        min={0}
                        max={Math.max(0, 100 - essentialsPercentage)}
                        step={1}
                      />
                    </div>

                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={personalBudgetData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, value }) => `${name}: $${Math.round(value)}`}
                          >
                            {personalBudgetData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `$${Math.round(value)}`} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="font-medium">Monthly Breakdown:</p>
                      <ul className="mt-2 space-y-1">
                        <li>Essentials: ${Math.round(monthlyIncome * (essentialsPercentage / 100))}</li>
                        <li>Discretionary: ${Math.round(monthlyIncome * (discretionaryPercentage / 100))}</li>
                        <li>Savings: ${Math.round(monthlyIncome * (savingsPercentage / 100))}</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Advanced Level Content */}
            <TabsContent value="advanced">
              <Card>
                <CardHeader>
                  <CardTitle>Advanced Budget Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Expense Categories */}
                    {Object.entries(monthlyExpenses).map(([category, amount]) => (
                      <div key={category} className="space-y-2">
                        <label className="text-sm font-medium">
                          {category.charAt(0).toUpperCase() + category.slice(1)}: ${amount}
                        </label>
                        <Slider
                          value={[amount]}
                          onValueChange={(values) => setMonthlyExpenses(prev => ({
                            ...prev,
                            [category]: values[0]
                          }))}
                          min={0}
                          max={category === 'housing' ? 3000 : 1000}
                          step={50}
                        />
                      </div>
                    ))}

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Monthly Spending Goal: ${monthlyGoal}</label>
                      <Slider
                        value={[monthlyGoal]}
                        onValueChange={(values) => setMonthlyGoal(values[0])}
                        min={2000}
                        max={8000}
                        step={100}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Emergency Fund Goal: ${emergencyFundGoal}</label>
                      <Slider
                        value={[emergencyFundGoal]}
                        onValueChange={(values) => setEmergencyFundGoal(values[0])}
                        min={5000}
                        max={30000}
                        step={1000}
                      />
                    </div>

                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={advancedBudgetData.expenses}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="amount"
                            label={({ category, percentage }) => `${category}: ${percentage}%`}
                          >
                            {advancedBudgetData.expenses.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `${Math.round(value)}`} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Budget Analysis Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Card className="p-4 bg-blue-50">
                        <h3 className="font-bold mb-2">Monthly Analysis</h3>
                        <div className="space-y-2">
                          <p>Total Expenses: ${advancedBudgetData.total}</p>
                          <p>Monthly Goal: ${monthlyGoal}</p>
                          <p className={advancedBudgetData.performance >= 0 ? "text-green-600" : "text-red-600"}>
                            {advancedBudgetData.performance >= 0 ? "Under" : "Over"} Budget by: $
                            {Math.abs(advancedBudgetData.performance)}
                          </p>
                        </div>
                      </Card>

                      <Card className="p-4 bg-green-50">
                        <h3 className="font-bold mb-2">Emergency Fund</h3>
                        <div className="space-y-2">
                          <p>Monthly Expenses (excl. savings): $
                            {advancedBudgetData.total - monthlyExpenses.savings}
                          </p>
                          <p>Emergency Fund Goal: ${emergencyFundGoal}</p>
                          <p>Covers {advancedBudgetData.emergencyFundMonths.toFixed(1)} months of expenses</p>
                        </div>
                      </Card>
                    </div>

                    {/* Recommendations */}
                    <Card className="p-4 bg-yellow-50">
                      <h3 className="font-bold mb-2">Budget Recommendations</h3>
                      <ul className="space-y-2">
                        {advancedBudgetData.expenses
                          .filter(expense => expense.percentage > 30 && expense.category !== 'Savings')
                          .map(expense => (
                            <li key={expense.category} className="text-red-600">
                              ⚠️ {expense.category} is {expense.percentage}% of your budget. Consider reducing this expense.
                            </li>
                          ))}
                        {monthlyExpenses.savings < (advancedBudgetData.total * 0.2) && (
                          <li className="text-red-600">
                            ⚠️ Savings rate is below 20%. Consider increasing your savings.
                          </li>
                        )}
                        {advancedBudgetData.emergencyFundMonths < 6 && (
                          <li className="text-red-600">
                            ⚠️ Emergency fund covers less than 6 months. Consider building a larger safety net.
                          </li>
                        )}
                        {advancedBudgetData.performance < 0 && (
                          <li className="text-red-600">
                            ⚠️ You're over your monthly budget. Look for areas to reduce spending.
                          </li>
                        )}
                        {advancedBudgetData.expenses
                          .filter(expense => expense.percentage < 5 && expense.category === 'Savings')
                          .map(expense => (
                            <li key={expense.category} className="text-red-600">
                              ⚠️ Your savings rate is very low at {expense.percentage}%. Try to increase this.
                            </li>
                          ))}
                      </ul>
                      {advancedBudgetData.performance >= 0 &&
                       monthlyExpenses.savings >= (advancedBudgetData.total * 0.2) &&
                       advancedBudgetData.emergencyFundMonths >= 6 && (
                        <p className="text-green-600 mt-2">
                          ✅ Your budget looks healthy! Keep maintaining these good financial habits.
                        </p>
                      )}
                    </Card>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default BudgetTraining;